import { FormEvent, useEffect, useRef, useState } from "react";
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { consultRuc, RucConsultation } from "../../api/ruc";
import { SUCCESS } from "../../utils/constants/tags";
import "./ruc-consult.scss";

interface Props {
  taxPayerId: number;
  onClose: () => void;
}

const RucConsultModal = ({ taxPayerId, onClose }: Props) => {
  const [ruc, setRuc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<RucConsultation | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setResult(null);
    setError("");
    if (!/^\d{5,8}(?:-\d)?$/.test(ruc.trim())) {
      setError("Ingrese un RUC de 5 a 8 dígitos, con o sin dígito verificador.");
      input.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const response = await consultRuc(taxPayerId, ruc.trim());
      if (!mounted.current) return;
      if (response.status === SUCCESS && response.data) {
        setResult(response.data);
      } else {
        setError(response.detail || "No se pudo consultar el RUC. Intente nuevamente.");
      }
    } catch {
      if (mounted.current) setError("No se pudo consultar el RUC. Revise su conexión e intente nuevamente.");
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  return (
    <CModal
      show
      onClose={onClose}
      onOpened={() => input.current?.focus()}
      centered
      scrollable
      className="ruc-consult-modal"
    >
      <CModalHeader closeButton>
        <CModalTitle>Consulta RUC</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p className="text-muted">Consulte los datos de un contribuyente en SIFEN.</p>
        <form id="ruc-consult-form" onSubmit={onSubmit} noValidate>
          <label htmlFor="ruc-consult-input">RUC</label>
          <div className="ruc-consult-search">
            <input
              ref={input}
              id="ruc-consult-input"
              className="form-control form-control-lg"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Ej.: 80012345-6"
              maxLength={10}
              value={ruc}
              disabled={submitting}
              aria-describedby="ruc-consult-help"
              aria-invalid={Boolean(error)}
              onChange={(event) => {
                setRuc(event.target.value);
                setError("");
                setResult(null);
              }}
            />
            <CButton
              type="submit"
              color="primary"
              size="lg"
              disabled={submitting}
              aria-label={submitting ? "Consultando..." : "Consultar RUC"}
              aria-busy={submitting}
              title="Consultar RUC"
            >
              {submitting ? (
                <Spinner animation="border" size="sm" aria-hidden="true" />
              ) : (
                <i className="fa fa-search" aria-hidden="true" />
              )}
            </CButton>
          </div>
          <small id="ruc-consult-help" className="form-text text-muted">
            Puede ingresar el RUC con o sin dígito verificador.
          </small>
        </form>
        {submitting && <p className="mt-3 mb-0" role="status">Consultando en SIFEN...</p>}
        {error && <div className="alert alert-danger mt-3 mb-0" role="alert">{error}</div>}
        {result && (
          <div className="mt-3" aria-live="polite">
            {result.found ? (
              <dl className="ruc-consult-result mb-0">
                <dt>RUC consultado</dt>
                <dd>{result.ruc}</dd>
                <dt>Nombre o razón social</dt>
                <dd>{result.businessName}</dd>
                <dt>Estado del RUC</dt>
                <dd>{result.statusDescription} ({result.statusCode})</dd>
              </dl>
            ) : (
              <div className="alert alert-warning mb-0" role="status">
                No se encontró el RUC {result.ruc} en SIFEN.
              </div>
            )}
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton type="button" color="secondary" onClick={onClose}>Cerrar</CButton>
      </CModalFooter>
    </CModal>
  );
};

export default RucConsultModal;
